/**
 * Upsell "Vous aimerez aussi" category widget — fetches the two category-rules
 * pools (pichon_brand + category_bestseller) from the transport-portal endpoint
 * and renders them as ONE row of up to 8 tiles: 3 Pichon (Rule 1) then 5 category
 * best-sellers (Rule 2), per spec §3/§5 ("Render displayed1 then displayed2,
 * hard-cap 8"). Independent of the FBT block and of Shopify's native recs.
 *
 * Render contract:
 *  - loading        → skeleton tiles, reserve height (no CLS), no event
 *  - error/ok:false → render nothing, fail closed (never block the PDP)
 *  - empty / < min  → render nothing (a 1-2 tile block reads as broken)
 *  - /cart.js fail  → render WITHOUT cart exclusion (degraded, acceptable)
 *  - ok             → render one carousel of up to 8 tiles (Rule 1 then Rule 2)
 *
 * Cross-block dedup: a shared window.__upsellDisplayed Set holds every product id
 * any upsell block has rendered on this PDP. This block reads it (FBT renders
 * above) and excludes those ids, then adds its own. Best-effort, never blocking.
 */
const DISPLAY_R1 = 3; // Rule 1 (Pichon) tiles
const DISPLAY_R2 = 5; // Rule 2 (category best-sellers) tiles
const MAX_TILES = 8;  // hard cap (3 + 5)
const MIN_ITEMS = 3;  // hide the whole block below this total

class UpsellCategory extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    this.trackEndpoint = this.dataset.trackEndpoint;
    this.productId = this.dataset.productId;
    this.minItems = parseInt(this.dataset.minItems || String(MIN_ITEMS), 10);
    this.root = this.querySelector('[data-upsell-root]');
    this.list = this.querySelector('[data-upsell-list]');
    this.viewport = this.querySelector('[data-upsell-viewport]');
    this.prevBtn = this.querySelector('[data-upsell-prev]');
    this.nextBtn = this.querySelector('[data-upsell-next]');

    if (!this.endpoint || !this.productId || !this.list) {
      this.#hide();
      return;
    }
    // Shared cross-block registry. FBT (above) may have already populated it.
    window.__upsellDisplayed ??= new Set();
    this.#load();
  }

  async #load() {
    this.#showSkeleton();

    let data;
    try {
      const url = `${this.endpoint}?product_id=${encodeURIComponent(this.productId)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) { this.#hide(); return; }
      data = await res.json();
    } catch {
      this.#hide(); // fail closed — never block the product page
      return;
    }
    if (!data || data.ok === false) { this.#hide(); return; }

    const pichon = Array.isArray(data.pichonBrand) ? data.pichonBrand : [];
    const bestseller = Array.isArray(data.categoryBestseller) ? data.categoryBestseller : [];

    // Exclusion set: current product + cart items + whatever FBT already showed.
    const exclude = new Set([String(this.productId)]);
    for (const id of window.__upsellDisplayed) exclude.add(String(id));
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      for (const it of cart.items || []) exclude.add(String(it.product_id));
    } catch {
      // /cart.js failed — proceed without cart exclusion (degraded, acceptable).
    }

    // Rule 1: walk the pool, skip excluded, take first DISPLAY_R1.
    const displayed1 = [];
    for (const r of pichon) {
      const id = String(r.productId);
      if (exclude.has(id)) continue;
      displayed1.push(r);
      exclude.add(id); // so Rule 2 can't repeat it
      if (displayed1.length >= DISPLAY_R1) break;
    }
    // Rule 2: skip excluded (now includes displayed1), take first DISPLAY_R2.
    const displayed2 = [];
    for (const r of bestseller) {
      const id = String(r.productId);
      if (exclude.has(id)) continue;
      displayed2.push(r);
      exclude.add(id);
      if (displayed2.length >= DISPLAY_R2) break;
    }

    // One ordered row: Rule 1 first, then Rule 2, hard-capped at 8.
    const tiles = [...displayed1, ...displayed2].slice(0, MAX_TILES);

    // Minimum-tile floor: a 1-2 tile recommendation block reads as broken.
    if (tiles.length < this.minItems) {
      this.#hide();
      return;
    }

    this.#render(tiles);
    if (this.root) this.root.hidden = false;

    // Register displayed ids so a late sibling can dedup against them.
    for (const r of tiles) window.__upsellDisplayed.add(String(r.productId));

    // Impression beacons (one per displayed tile, carrying its own source/rank).
    this.#beaconImpression(tiles);
  }

  #showSkeleton() {
    const tiles = Array.from({ length: DISPLAY_R1 }, () =>
      `<li class="upsell-category__tile upsell-category__tile--skeleton" aria-hidden="true">
         <div class="upsell-category__link">
           <div class="upsell-category__img-skeleton"></div>
           <div class="upsell-category__line-skeleton"></div>
           <div class="upsell-category__line-skeleton upsell-category__line-skeleton--short"></div>
         </div>
       </li>`
    ).join('');
    this.list.innerHTML = tiles;
    if (this.root) this.root.hidden = false;
  }

  #render(recs) {
    this.list.innerHTML = recs.map((r) => this.#tile(r)).join('');
    if (this.root) this.root.hidden = false;
    this.#guardImages();
    this.#setupCarousel();
    this.#bindClicks();
  }

  #tile(r) {
    const money = (price) => {
      if (price == null || price === '') return '';
      const n = Number(price);
      if (Number.isNaN(n)) return '';
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
    };
    const img = r.imageUrl
      ? `<img class="upsell-category__img" data-upsell-img src="${this.#esc(r.imageUrl)}" alt="" loading="lazy" width="160" height="160">`
      : `<div class="upsell-category__img upsell-category__img--placeholder" aria-hidden="true"></div>`;
    const ht = money(r.price);
    const ttc = money(r.priceTtc);
    let priceBlock = '';
    if (ttc) {
      priceBlock = `<div class="upsell-category__price">
        <span class="upsell-category__price-ttc">${ttc}<span class="upsell-category__tax">TTC</span></span>
        ${ht ? `<span class="upsell-category__price-ht">${ht}<span class="upsell-category__tax">HT</span></span>` : ''}
      </div>`;
    } else if (ht) {
      priceBlock = `<div class="upsell-category__price"><span class="upsell-category__price-ttc">${ht}<span class="upsell-category__tax">HT</span></span></div>`;
    }
    const href = `/products/${this.#esc(r.handle)}`;
    return `<li class="upsell-category__tile">
      <a class="upsell-category__link" href="${href}" aria-label="Voir ${this.#esc(r.title)}"
         data-upsell-tile data-source="${this.#esc(r.source)}" data-rank="${this.#esc(r.rank)}" data-pid="${this.#esc(r.productId)}">
        ${img}
        <span class="upsell-category__title">${this.#esc(r.title)}</span>
        ${priceBlock}
      </a>
    </li>`;
  }

  #guardImages() {
    this.list.querySelectorAll('[data-upsell-img]').forEach((img) => {
      const swap = () => img.classList.add('upsell-category__img--placeholder');
      if (img.complete && img.naturalWidth === 0) swap();
      else img.addEventListener('error', swap, { once: true });
    });
  }

  #setupCarousel() {
    const vp = this.viewport;
    const list = this.list;
    const prevBtn = this.prevBtn;
    const nextBtn = this.nextBtn;
    if (!vp || !prevBtn || !nextBtn) return;

    const step = () => {
      const tile = list.querySelector('.upsell-category__tile');
      const gap = 20;
      return tile ? tile.getBoundingClientRect().width + gap : vp.clientWidth * 0.8;
    };
    prevBtn.addEventListener('click', () => vp.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    nextBtn.addEventListener('click', () => vp.scrollBy({ left: step() * 2, behavior: 'smooth' }));

    const update = () => {
      const overflowing = vp.scrollWidth - vp.clientWidth > 4;
      prevBtn.hidden = !overflowing;
      nextBtn.hidden = !overflowing;
      if (!overflowing) return;
      prevBtn.disabled = vp.scrollLeft <= 2;
      nextBtn.disabled = vp.scrollLeft >= vp.scrollWidth - vp.clientWidth - 2;
    };
    vp.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    list.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });
    update();
  }

  // --- Tracking beacons (block: "category"). Fire-and-forget, never block. ---
  #bindClicks() {
    this.list.querySelectorAll('[data-upsell-tile]').forEach((a) => {
      a.addEventListener('click', () => {
        this.#beacon({
          eventType: 'click',
          block: 'category',
          targetProductId: this.productId,
          recommendedProductId: a.dataset.pid,
          source: a.dataset.source,
          rank: parseInt(a.dataset.rank || '0', 10),
        });
      });
    });
  }

  #beaconImpression(recs) {
    for (const r of recs) {
      this.#beacon({
        eventType: 'impression',
        block: 'category',
        targetProductId: this.productId,
        recommendedProductId: r.productId,
        source: r.source,
        rank: r.rank,
      });
    }
  }

  #beacon(payload) {
    if (!this.trackEndpoint) return;
    try {
      const body = JSON.stringify({ ...payload, sessionId: this.#sessionId() });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.trackEndpoint, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(this.trackEndpoint, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } });
      }
    } catch {
      // beacons are best-effort
    }
  }

  #sessionId() {
    try {
      let sid = localStorage.getItem('upsell_sid');
      if (!sid) {
        sid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(36).slice(2);
        localStorage.setItem('upsell_sid', sid);
      }
      return sid;
    } catch {
      return null;
    }
  }

  #hide() {
    if (this.root) this.root.hidden = true;
    if (this.list) this.list.innerHTML = '';
  }

  #esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('upsell-category')) {
  customElements.define('upsell-category', UpsellCategory);
}
